'use client'

import React from 'react'
import { Box, Button, Flex, Image, Link, Text } from '@chakra-ui/react'
import { formatLykCoins } from '@/lib/lyk-coin'
import {
  AdvanceRecoveryTimelineResponse,
} from '@/src/queries/earnings/advance-recovery-timeline'

const formatUsdAmount = (value: number) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)

const formatCoinAmount = (value: number) =>
  Number(formatLykCoins(value)).toLocaleString('en-US')

interface CreatorAdvancesContentProps {
  advanceRecoveryTimeline?: AdvanceRecoveryTimelineResponse
  isLoading: boolean
  isError: boolean
}

const CreatorAdvancesContent = ({
  advanceRecoveryTimeline,
  isLoading,
  isError,
}: CreatorAdvancesContentProps) => {
  const estimatedTotalUsd =
    advanceRecoveryTimeline?.estimated_total_earnings?.usd_amount ?? 0
  const estimatedTotalCoins =
    advanceRecoveryTimeline?.estimated_total_earnings?.lyk_coins ?? 0
  const netEstimatedUsd =
    advanceRecoveryTimeline?.outstanding_amount?.usd_amount ?? 0
  const netEstimatedCoins =
    advanceRecoveryTimeline?.outstanding_amount?.lyk_coins ?? 0
  const breakdownItems = advanceRecoveryTimeline?.breakdown ?? []

  return (
    <>
      <Box
        border="1px solid #BFEFE5"
        bg="linear-gradient(90deg, rgba(241, 251, 248, 0.9) 0%, rgba(232, 249, 244, 0.9) 100%)"
        borderRadius="16px"
        px={{ base: 4, sm: 5 }}
        py={{ base: 3.5, sm: 4 }}
        mb={4}
      >
        <Flex align="center" justify="space-between" gap={4}>
          <Box>
            <Flex align="center" gap={1.5} color="#656565">
              <Text
                fontSize={{ base: 16, sm: 16, md: 18 }}
                fontWeight={700}
                lineHeight={{ base: 1.3, sm: 'normal' }}
              >
                Estimated Total Earnings
              </Text>
              <Image
                boxSize={{ base: 3.5, sm: 4 }}
                src="/rewards/breakdown/info-green.svg"
                alt="Info"
              />
            </Flex>
            <Text
              color="#797979"
              fontSize={{ base: 13, sm: 14 }}
              lineHeight={1.3}
            >
              Lifetime creator earnings from watch time
            </Text>
          </Box>

          <Box textAlign="right">
            <Text
              color="lyk-green"
              fontSize={{ base: 20, sm: 22, md: 24 }}
              fontWeight={700}
            >
              US$ {formatUsdAmount(estimatedTotalUsd)}
            </Text>
            <Text
              color="#8C8C8C"
              fontSize={{ base: 13, sm: 15 }}
              fontWeight={500}
            >
              {formatCoinAmount(estimatedTotalCoins)} LYK Coins
            </Text>
          </Box>
        </Flex>
      </Box>

      {breakdownItems.length > 0 ? (
        <Box
          maxH={{ base: '420px', sm: '500px' }}
          overflowY="auto"
          pr={1}
          mb={6}
          sx={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#BDBDBD #F3F3F3',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#F3F3F3',
              borderRadius: '999px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#BDBDBD',
              borderRadius: '999px',
            },
          }}
        >
          {breakdownItems.map((payment, index) => (
            <React.Fragment
              key={payment.id ?? payment.date ?? payment.issued_on ?? index}
            >
              <div className="mb-3 flex items-center">
                <div className="flex-grow border-t-2 border-[#ECECEC]"></div>
                <span className="mx-1.5 rounded-md bg-[#D9D9D9] px-2 py-0.5 text-xs font-semibold text-[#797979] sm:px-3 sm:text-sm">
                  {payment.date_label ?? payment.date ?? '-'}
                </span>
                <div className="flex-grow border-t-2 border-[#ECECEC]"></div>
              </div>

              <Flex className="mb-5 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Flex align="center" gap={3}>
                  <Flex
                    align="center"
                    justify="center"
                    boxSize={{ base: 10, sm: 12 }}
                    borderRadius="full"
                    bg="#EAF9F4"
                  >
                    <Image
                      boxSize={{ base: 4, sm: 5 }}
                      src="/rewards/breakdown/calendarIcon.svg"
                      alt="Advance"
                    />
                  </Flex>

                  <Box>
                    <Text
                      color="#656565"
                      fontSize={{ base: 18, sm: 18, md: 22 }}
                      fontWeight={500}
                      lineHeight={{ base: 1.3, sm: 'normal' }}
                    >
                      Advance Recovery
                    </Text>
                    <Text color="#797979" fontSize={{ base: 13, sm: 14 }}>
                      Recovery recorded on{' '}
                      {payment.issued_on_label ??
                        payment.issued_on ??
                        payment.date ??
                        '-'}
                    </Text>
                  </Box>
                </Flex>

                <Box textAlign={{ base: 'left', sm: 'right' }}>
                  <Text
                    color="#F48B82"
                    fontSize={{ base: 20, sm: 22, md: 24 }}
                    fontWeight={700}
                  >
                     US$ {formatUsdAmount(payment.recovered_amount)}
                  </Text>
                  <Text color="#797979" fontSize={{ base: 13, sm: 14 }}>
                    Recovered to date:{' '}
                    <Text
                      as="span"
                      color={
                        payment.recovered_to_date === 0 ? '#797979' : 'lyk-green'
                      }
                      fontWeight={500}
                    >
                      US$ {formatUsdAmount(payment.recovered_to_date)}
                    </Text>
                  </Text>
                </Box>
              </Flex>
            </React.Fragment>
          ))}
        </Box>
      ) : isLoading ? (
        <Text color="#8C8C8C" fontSize={{ base: 15, sm: 14 }} mb={6}>
          Loading advance recovery timeline...
        </Text>
      ) : isError ? (
        <Text color="#8C8C8C" fontSize={{ base: 15, sm: 14 }} mb={6}>
          Unable to load advance recovery timeline right now.
        </Text>
      ) : (
        <Text color="#8C8C8C" fontSize={{ base: 15, sm: 14 }} mb={6}>
          No advance recovery activity available yet.
        </Text>
      )}

      <Box
        border="1px solid #F7C9C5"
        bg="#FFF5F4"
        borderRadius="16px"
        px={{ base: 4, sm: 5 }}
        py={{ base: 3.5, sm: 4 }}
        mb={3}
      >
        <Flex align="center" justify="space-between" gap={4}>
          <Box>
            <Text
              color="#F48B82"
              fontSize={{ base: 18, sm: 18, md: 22 }}
              fontWeight={700}
              lineHeight={{ base: 1.3, sm: 'normal' }}
            >
              NET ESTIMATED EARNINGS
            </Text>
            <Text
              color="#797979"
              fontSize={{ base: 13, sm: 14 }}
              lineHeight={1.3}
            >
              Total earnings minus all outstanding advances
            </Text>
          </Box>

          <Box textAlign="right">
            <Text
              color="#F48B82"
              fontSize={{ base: 20, sm: 22, md: 24 }}
              fontWeight={700}
            >
              US$ {formatUsdAmount(netEstimatedUsd)}
            </Text>
            <Text color="#797979" fontSize={{ base: 13, sm: 14 }} fontWeight={500}>
              {formatCoinAmount(netEstimatedCoins)} LYK Coins after outstanding recovery
            </Text>
          </Box>
        </Flex>
      </Box>

      <Flex mt={4} align="center" gap={2} mb={6}>
        <Image
          src="/earnings/Info.png"
          alt="Info"
          boxSize={{ base: '14px', sm: '16px' }}
        />
        <Text color="#8C8C8C" fontSize={{ base: 13, sm: 14 }}>
          An advance is recovered only from your LYKSTAGE earnings - never billed
        to you
        </Text>
      </Flex>

      <Flex className="justify-end">
        <Button
          className="no-underline max-sm:w-full"
          py={5}
          px={10}
          fontSize={{ base: 14, sm: 16 }}
          bg="lyk-green"
          as={Link}
          href="/advancepayment"
          color="white"
          fontWeight={{ base: 400, sm: 500 }}
          textDecoration={'none'}
          _hover={{ opacity: 0.7, textDecoration: 'none' }}
        >
          View Advance Details
        </Button>
      </Flex>
    </>
  )
}

export default CreatorAdvancesContent
